import { useQuery } from '@tanstack/react-query';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Organization } from '../../types/organization.types';

// Extended organization type with tenant info
export interface OrganizationWithTenant extends Organization {
  hasTenant?: boolean;
}

export const useOrganizationsData = () => {
  // Query for all organizations and tenant IDs (client-side filtering)
  const dataQuery = useQuery<OrganizationWithTenant[]>({
    queryKey: ['Organizations'],
    queryFn: async () => {
      try {
        // Fetch organizations
        const organizationsRef = collection(db, 'Organizations');
        const orgsQuery = query(organizationsRef, orderBy('org_name', 'asc'));
        const orgsSnapshot = await getDocs(orgsQuery);

        // Fetch all tenant IDs
        const tenantsRef = collection(db, 'tenants');
        const tenantsSnapshot = await getDocs(tenantsRef);
        const tenantIds = new Set(tenantsSnapshot.docs.map((doc) => doc.id));

        const organizations = orgsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          hasTenant: tenantIds.has(doc.id),
        })) as OrganizationWithTenant[];

        // Filter out soft-deleted organizations
        return organizations.filter((org) => org.deleted !== true);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 30, // Consider data fresh for 30 minutes
    gcTime: 1000 * 60 * 60, // Keep unused data in cache for 60 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    data: dataQuery.data || [],
    isLoading: dataQuery.isLoading || dataQuery.isFetching,
    error: dataQuery.error,
    refetch: dataQuery.refetch,
    totalCount: dataQuery.data?.length || 0,
  };
};
